<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import mdiStarOutline from '~icons/mdi/star-outline'
import { useUserFavorites } from '@/composables/useUserFavorites'
import { formatDateTime } from '@/utils/format'
import type { Resource } from '@/types/_general'

interface FlattenedFavorite {
  id: string
  resource: Resource
  title: string
  added: Date | string
  displayTitle: string
}

const { t } = useI18n()
const { userFavorites, isLoading, toggleFavorite } = useUserFavorites()

const activeTab = ref<Resource | 'all'>('all')

const flattenedFavorites = computed<FlattenedFavorite[]>(() => {
  const items: FlattenedFavorite[] = []
  for (const [resource, favorites] of Object.entries(userFavorites.value)) {
    for (const fav of favorites) {
      items.push({
        id: fav.id,
        resource: resource as Resource,
        title: fav.title,
        added: fav.added,
        displayTitle: resource === 'cases' ? `#${fav.title}` : fav.title,
      })
    }
  }
  return items.sort((a, b) => new Date(b.added).getTime() - new Date(a.added).getTime())
})

const tabs = computed(() =>
  Object.entries(userFavorites.value)
    .filter(([, favorites]) => favorites.length > 0)
    .map(([resource]) => ({
      value: resource as Resource,
      label: t(`common.${resource}`),
    }))
)

const filteredItems = computed(() => {
  if (activeTab.value === 'all') return flattenedFavorites.value
  return flattenedFavorites.value.filter((item) => item.resource === activeTab.value)
})

watch(
  () => userFavorites.value,
  () => {
    if (activeTab.value === 'all') return
    const currentResourceFavorites = userFavorites.value[activeTab.value]
    if (currentResourceFavorites == null || currentResourceFavorites.length === 0) {
      activeTab.value = 'all'
    }
  },
  { deep: true }
)
</script>

<template>
  <section
    v-if="isLoading"
    class="d-flex align-center justify-center h-screen"
  >
    <v-progress-circular
      indeterminate
      color="brand-primary"
    />
  </section>

  <section v-else-if="flattenedFavorites.length > 0">
    <v-chip-group
      v-model="activeTab"
      class="d-flex flex-wrap gap-2 pa-4"
      mandatory
      column
    >
      <v-chip
        value="all"
        elevation="0"
        variant="flat"
        class="text-body-medium text-capitalize border"
        :class="activeTab === 'all' ? 'bg-brand-primary' : 'bg-background'"
        pill
        label
      >
        {{ t('common.all') }}
      </v-chip>

      <v-chip
        v-for="tab in tabs"
        :key="tab.value"
        :value="tab.value"
        elevation="0"
        variant="flat"
        class="text-body-medium text-capitalize border"
        :class="activeTab === tab.value ? 'bg-brand-primary' : 'bg-background'"
        pill
        label
      >
        {{ tab.label }}
      </v-chip>
    </v-chip-group>

    <v-divider class="mb-5" />

    <div v-if="filteredItems.length > 0">
      <article
        v-for="item in filteredItems"
        :key="`${item.resource}-${item.id}`"
        class="px-4 pb-4"
      >
        <v-row class="py-2 align-center">
          <v-col cols="9">
            <router-link
              :to="{ name: 'ItemDisplay', params: { resource: item.resource, resourceId: item.id } }"
              class="d-block text-decoration-none text-high-emphasis"
            >
              <span class="text-uppercase text-body-small">{{ t(`common.${item.resource}`) }}</span>
              <h2 class="text-headline-medium text-truncate">{{ item.displayTitle }}</h2>
              <span class="text-body-small">{{ formatDateTime(item.added) }}</span>
            </router-link>
          </v-col>
          <v-col class="text-right">
            <v-btn
              class="text-body-medium font-weight-bold text-decoration-none"
              elevation="0"
              variant="flat"
              :aria-label="t('common.remove')"
              @click="void toggleFavorite(item.resource, { _id: item.id, reference_number: item.title })"
            >
              {{ t('common.remove') }}
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="mt-3" />
      </article>
    </div>
  </section>

  <section
    v-else
    class="d-flex flex-column align-center text-center justify-center opacity-50 pa-10"
  >
    <h2 class="text-body-large mb-4">{{ t('common.empty_watch_list') }}</h2>
    <p class="text-body-large">
      {{ t('common.empty_watch_list_prompt') }}
      (<v-icon
        :icon="mdiStarOutline"
        aria-hidden="true"
      />)
    </p>
  </section>
</template>
